import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  CheckCircle,
  Calendar,
  DollarSign,
  Clock,
  MapPin,
  ArrowRight,
  Phone,
  TrendingUp
} from 'lucide-react';

export default function DealsPage() {
  const { t } = useLanguage();

  const closedDeals = [
    {
      id: 1,
      amount: '$2,500,000',
      closingTime: '1 Week',
      date: 'March 2026',
      type: 'Commercial Loan',
      property: 'Apartment Complex',
      location: 'West Los Angeles, CA',
      highlights: [
        'Apartment complex financing',
        'Closed in just 7 days',
        'No appraisal required',
        'Asset-based approval'
      ]
    },
    {
      id: 2,
      amount: '$1,500,000',
      closingTime: '1 Week',
      date: 'March 2026',
      type: 'Bridge Loan',
      property: 'Commercial Property',
      location: 'Los Angeles, CA',
      highlights: [
        'Commercial property financing',
        'Fast bridge loan approval',
        'No appraisal required',
        'Smooth closing process'
      ]
    },
    {
      id: 3,
      amount: '$225,000',
      closingTime: '1 Week',
      date: 'March 6, 2026',
      type: 'Bridge Loan',
      property: 'Residential Property',
      location: 'Carson, CA',
      highlights: [
        'Closed in just 7 days',
        'No appraisal required',
        'Asset-based approval',
        'Smooth closing process'
      ]
    },
  ];

  return (
    <div className="pt-20" data-testid="deals-page">
      {/* Hero Section */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
              PROOF OF PERFORMANCE
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Recently Closed Deals
            </h1>
            <p className="font-body text-slate-300 text-lg">
              See our track record of fast closings and successful funding. We deliver results when our clients need them most.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-amber-600 py-6">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="font-heading text-3xl font-bold text-white">$4.2M+</p>
              <p className="font-body text-amber-100 text-sm">Recently Funded</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-bold text-white">7 Days</p>
              <p className="font-body text-amber-100 text-sm">Average Closing</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-bold text-white">100%</p>
              <p className="font-body text-amber-100 text-sm">Client Satisfaction</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-bold text-white">No</p>
              <p className="font-body text-amber-100 text-sm">Appraisal Required</p>
            </div>
          </div>
        </div>
      </section>

      {/* Deals Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Our Recent Success Stories
            </h2>
            <p className="font-body text-slate-600 max-w-2xl mx-auto">
              Every deal represents a real estate investor who trusted us to deliver fast, reliable funding.
            </p>
          </div>

          <div className="space-y-8">
            {closedDeals.map((deal) => (
              <div 
                key={deal.id} 
                className="bg-slate-50 border border-slate-200 overflow-hidden"
                data-testid={`deal-${deal.id}`}
              >
                <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-heading text-xl font-bold text-white">DEAL CLOSED</span>
                  </div>
                  <span className="font-body text-amber-500 font-semibold">{deal.date}</span>
                </div>
                
                <div className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Deal Details */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <DollarSign className="w-8 h-8 text-amber-600" />
                        <span className="font-heading text-4xl font-bold text-slate-900">{deal.amount}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-white p-4 border border-slate-200">
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <Clock className="w-4 h-4" />
                            <span className="font-body text-sm">Closing Time</span>
                          </div>
                          <p className="font-heading text-xl font-bold text-slate-900">{deal.closingTime}</p>
                        </div>
                        <div className="bg-white p-4 border border-slate-200">
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <TrendingUp className="w-4 h-4" />
                            <span className="font-body text-sm">Loan Type</span>
                          </div>
                          <p className="font-heading text-xl font-bold text-slate-900">{deal.type}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-slate-600 mb-6">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-amber-600" />
                          <span className="font-body">{deal.location}</span>
                        </div>
                        {deal.property && (
                          <div className="flex items-center gap-2">
                            <span className="text-slate-300">|</span>
                            <span className="font-body font-semibold text-slate-700">{deal.property}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Highlights */}
                    <div>
                      <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">Deal Highlights</h3>
                      <ul className="space-y-3">
                        {deal.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <span className="font-body text-slate-700">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <div className="mt-8 p-4 bg-amber-50 border border-amber-200">
                        <p className="font-body text-amber-800 text-sm">
                          <strong>Fast Funding:</strong> This deal closed in just 1 week from application to funding, demonstrating our commitment to speed and efficiency.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="font-body text-slate-300 text-lg mb-8">
              Get funded in as little as 5-7 days. No appraisal required. Contact us today to discuss your deal.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/apply">
                <Button size="lg" className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8">
                  Apply Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:424-303-2130">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-slate-900 font-semibold px-8">
                  <Phone className="mr-2 w-5 h-5" />
                  424-303-2130
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
